import { create } from 'zustand';
import { apiRequest } from '../lib/api';
import { ApiError } from '../lib/http';
import { CustomerUser, DeliveryAddressInput } from '../types/api';

interface SignupInput {
  readonly delivery_address?: DeliveryAddressInput;
  referral_code?: string;
  name: string;
  email: string;
  phone: string;
  password: string;
}

interface AuthState {
  user: CustomerUser | null;
  status: 'unknown' | 'guest' | 'authed';
  loading: boolean;
  error: string | null;
  fetchMe: () => Promise<void>;
  login: (identifier: string, password: string) => Promise<boolean>;
  signup: (input: SignupInput) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const LOGIN_FAILED = 'Login failed. Please try again.';
const SIGNUP_FAILED = 'Signup failed. Please try again.';

/** Server-provided messages are shown; network failures fall back to a generic message. */
const errorMessage = (error: unknown, fallback: string) =>
  error instanceof ApiError ? error.message : fallback;

const loginBody = (identifier: string, password: string) =>
  identifier.includes('@') ? { email: identifier, password } : { phone_number: identifier, password };

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  status: 'unknown',
  loading: false,
  error: null,

  fetchMe: async () => {
    try {
      set({ user: await apiRequest<CustomerUser>('/user/me'), status: 'authed' });
    } catch {
      set({ user: null, status: 'guest' });
    }
  },

  login: async (identifier: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const user = await apiRequest<CustomerUser>('/user/login', {
        method: 'POST',
        json: loginBody(identifier, password),
        fallbackError: LOGIN_FAILED,
      });
      set({ user, status: 'authed', loading: false });
      return true;
    } catch (error) {
      set({ loading: false, error: errorMessage(error, LOGIN_FAILED) });
      return false;
    }
  },

  signup: async ({ name, email, phone, password, delivery_address, referral_code }: SignupInput) => {
    set({ loading: true, error: null });
    try {
      await apiRequest<CustomerUser>('/user/signup', {
        method: 'POST',
        json: {
          ...(delivery_address ? { delivery_address } : {}),
          referral_code: referral_code || undefined,
          first_name: name,
          email,
          phone_number: phone,
          password,
        },
        fallbackError: SIGNUP_FAILED,
      });
      // Signup does not set the session cookie; log in right away.
      const loggedIn = await get().login(email, password);
      if (!loggedIn) return false;
      set({ loading: false });
      return true;
    } catch (error) {
      set({ loading: false, error: errorMessage(error, SIGNUP_FAILED) });
      return false;
    }
  },

  logout: async () => {
    try {
      await apiRequest<void>('/user/logout', { method: 'POST' });
    } catch (error) {
      // An error response still ends the local session; only network failures propagate.
      if (!(error instanceof ApiError)) throw error;
    } finally {
      set({ user: null, status: 'guest' });
    }
  },

  clearError: () => set({ error: null }),
}));
